import { useItemStore } from "@/itemStore";
import type { Item, Term, Year } from "@/itemStore";
import { fetchTerms, fetchMonth } from "@/fetch";

export function populateSettings(
  filters: HTMLElement,
  posts: HTMLElement
): void {
  const itemStore = useItemStore();
  itemStore.showSearch = filters.dataset.search == "1";
  itemStore.showSort = filters.dataset.sort == "1";
  itemStore.filterRadio = filters.dataset.radio == "1";
  itemStore.showImage = posts.dataset.image != "0";
  itemStore.showPreview = posts.dataset.preview != "0";
  itemStore.perPage = Number(posts.dataset.posts) ?? 8;
  itemStore.showFields = (posts.dataset.fields ?? "author,date,term").split(
    ","
  );
  itemStore.hasLink = posts.dataset.haslink != "0";
  itemStore.show = itemStore.perPage;
  if (posts.dataset.sort != undefined) {
    itemStore.sort = posts.dataset.sort;
  }
  itemStore.taxonomy.primary = filters.dataset.primary ?? "";
  itemStore.taxonomy.secondary = filters.dataset.secondary ?? "";
  itemStore.readMore = posts.dataset.readmore ?? "";
  // Set initial value.
  itemStore.asc = itemStore.sort == "title";

  if (itemStore.taxonomy.primary) {
    fetchTerms(itemStore.taxonomy.primary);
  }
  if (itemStore.taxonomy.secondary) {
    fetchTerms(itemStore.taxonomy.secondary);
  }

  if (itemStore.showMonth) {
    fetchMonth();
  }

  if (window.location.hash) {
    setFilterFromHash(window.location.hash.substring(1));
  }
  window.addEventListener("popstate", () => {
    setFilterFromHash(window.location.hash.substring(1));
  });
}

function setFilterFromHash(hash: string): void {
  const itemStore = useItemStore();
  if (itemStore.historyInhibit) {
    // This history event is caused by the mutation, and should be ignored.
    itemStore.historyInhibit = false;
  } else {
    const parts = hash.split("/");
    if (parts[0]) {
      itemStore.filter.primary = parts[0].split(",");
    }
    if (parts[1]) {
      itemStore.filter.secondary = parts[1].split(",");
    }
  }
}

export const loadMoreHandler = () => {
  const button = document.getElementById("dynamic-archive-load-more");
  const shown = document.getElementById("dynamic-archive-shown");
  const total = document.getElementById("dynamic-archive-total");

  const itemStore = useItemStore();
  if (button !== null) {
    button.addEventListener("click", () => {
      itemStore.showMore();
    });
  }
  itemStore.$subscribe(() => {
    if (button !== null) {
      if (itemStore.hasMore) {
        button.classList.remove("hidden");
      } else {
        button.classList.add("hidden");
      }
    }
    if (shown !== null) {
      shown.innerText = itemStore.getItems.length.toString();
    }
    if (total !== null) {
      total.innerText = itemStore.total.toString();
    }
  });
};

export const sortTerms = (terms: Array<Term>): Array<Term> => {
  return terms
    .filter((term: Term) => {
      return term.order > -1;
    })
    .sort((a: Term, b: Term) => {
      return a.order - b.order;
    });
};

/**
 * Gets stored values from session storage.
 * This is only safe to run on an empty store.
 */
export const initializeSessionStorage = (type: string, lang: string): void => {
  const itemStore = useItemStore();
  if (type && lang) {
    itemStore.type = type;
    itemStore.lang = lang;
    const key = itemStore.type + "_" + itemStore.lang;
    const json = sessionStorage.getItem(key);
    if (json !== null) {
      const data = JSON.parse(json);
      itemStore.items = data.items as Array<Item>;
      itemStore.terms.primary = data.terms.primary as Array<Term>;
      itemStore.terms.secondary = data.terms.secondary as Array<Term>;
      itemStore.months = data.months as Array<Year>;
    }
  }
};

/**
 * Saves some store values to session storage.
 * Includes store subscriber, so should only be run once.
 */
export const sessionStorageListener = (): void => {
  const itemStore = useItemStore();
  if (itemStore.type && itemStore.lang) {
    const key = itemStore.type + "_" + itemStore.lang;
    itemStore.$subscribe(() => {
      const json = JSON.stringify({
        terms: itemStore.terms,
        months: itemStore.months,
        items: itemStore.items,
      });
      sessionStorage.setItem(key, json);
    });
  }
};
